import {FC, useCallback, useEffect, useState} from "react";
import {useLocation, useParams} from 'react-router-dom';
// pages
import EstimationPage from "./EstimationPage";
import PaymentConfirmationPage from "./PaymentConfirmationPage";
import { IPurchase, IQuote } from "../@interfaces/purchase.interface";
import { IBasket } from "../@interfaces/basket.interface";
import EntryPage from "./EntryPage";
import PaymentPage from "./PaymentPage";
// styles
import styles from "../styles/pages/FlexibleOffsetWidget.module.css";
// api
import {createQuoteByCo2, createQuoteByMoney} from "../api/quoteApi";
import { getBasketById } from "../api/projectApi";
import { startPurchaseByQuote } from "../api/purchaseApi";
import { getConfigByMerchantCode } from "../utils/merchantConfig";
import {getConfiguration, IWidgetConfiguration} from "../merchantConfig";
// utils
import {DEFAULT_CURRENCY, SUPPORTED_CURRENCIES} from "../utils/currency";
import { sendAnalyticsEvent, setWidgetAnalyticEventParams } from "../utils/analytics";


const ContributionWidget: FC = () => {
  const [widgetConfiguration, setWidgetConfiguration] = useState<IWidgetConfiguration | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [quote, setQuote] = useState<IQuote | null>(null);
  const [basket, setBasket] = useState<IBasket | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [purchase, setPurchase] = useState<IPurchase | null>(null);
  const [clientSecret, setClientSecret] = useState("");
  const [monetaryAmount, setMonetaryAmount] = useState<number>(0);
  const [co2AmountKg, setCo2AmountKg] = useState<number>(0);
  const [selectedCurrency, setSelectedCurrency] = useState('');
  const [entryOptionSelected, setEntryOptionSelected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isZeroDecimalCurrency, setIsZeroDecimalCurrency] = useState(false);
  const [appleGooglePayPaymentRequestData, setAppleGooglePayPaymentRequestData] = useState<any>();
  const [quotingErrorMessage, setQuotingErrorMessage] = useState<string | null>(null);
  const [skipViewItemAnalyticsEvent, setSkipViewItemAnalyticEvent] = useState(true);
  const params= useParams()

  const search = new URLSearchParams(useLocation().search);
  const currencyFromUrl = search.get("currency");
  const amountFromUrl = search.get("amount");
  const basketFromUrl = search.get("basketCode");
  const emailFromUrl = search.get("email");
  const language = search.get("lang");
  const skipToPaymentParam = search.get("skipToPayment");
  const onPaymentSuccessCallbackParam = search.get("onPaymentSuccessCallback");

  const merchantCode = params.merchantCode || search.get("merchantCode")  || 'CARBONCLICK';
  const programmeCode = params.programmeCode || search.get("programmeCode");

  useEffect(() => {
    setLoading(true);
    if (merchantCode) {
      getConfigByMerchantCode(merchantCode)
          .finally(() => {
              setWidgetAnalyticEventParams({
                merchant_code: merchantCode,
                programme_code: programmeCode,
                widget_type: "Contribution"
              });
              sendAnalyticsEvent('widget_loaded');
              return setLoading(false);
          });
      setWidgetConfiguration(getConfiguration(merchantCode));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantCode])

  useEffect(() => {
    if(!monetaryAmount && !co2AmountKg) {
      setQuote(null);
    }
  }, [monetaryAmount, co2AmountKg])

  useEffect(() => {
    if(amountFromUrl) {
      setMonetaryAmount(Number(amountFromUrl));
    } else {
      setMonetaryAmount(widgetConfiguration?.defaultOffsetAmount || 5);
    }
  }, [amountFromUrl, widgetConfiguration])

  useEffect(() => {
    if(emailFromUrl) {
      setUserEmail(emailFromUrl);
    }
  }, [emailFromUrl])

  useEffect(() => {
    if (currencyFromUrl && SUPPORTED_CURRENCIES.includes(currencyFromUrl.toUpperCase())) {
      setSelectedCurrency(currencyFromUrl.toUpperCase());
    } else {
      setSelectedCurrency(widgetConfiguration?.defaultCurrency || DEFAULT_CURRENCY);
    }
  }, [currencyFromUrl, widgetConfiguration]);

  const makeQuote = useCallback((moneyAmount: number, currency: string, merchantCode: string, promiseOptions?: {cancel: boolean}) => {
    setIsLoading(true);
    setQuotingErrorMessage(null);
    (async () => {
      const quoteResponse = await createQuoteByMoney({
        merchantCode: merchantCode,
        basketCode: basketFromUrl,
        amount: {
          currency: currency,
          value: moneyAmount
        },
        paymentProvider: "STRIPE",
        programmeCode: programmeCode
      });
      if (promiseOptions?.cancel) return;
      const quoteResult = quoteResponse.data;
      if (quoteResult?.quoteId) {
        quoteResult.requestedFor = "MONEY"
        await processCreatedQuote(quoteResult, promiseOptions);
      }
    })()
        .catch(handleMakeQuoteError)
        .finally(() => setIsLoading(false));
  }, [basketFromUrl, programmeCode]);


  const makeQuoteCo2 = (amount: number, currency: string, merchantCode: string, updateMonetaryAmount: boolean = false, promiseOptions?: {cancel: boolean}) => {
      setIsLoading(true);
      setQuotingErrorMessage(null);
      (async () => {
        const quoteResponse = await createQuoteByCo2({
          merchantCode: merchantCode,
          basketCode: basketFromUrl,
          co2Quantity: String(amount),
          paymentProvider: "STRIPE",
          currency: currency,
          activityType: "FIXED_OFFSET",
          programmeCode: programmeCode
        });
        if (promiseOptions?.cancel) return;
        const quoteResult = quoteResponse.data;
        if (quoteResult?.quoteId) {
          if (updateMonetaryAmount) setMonetaryAmount(quoteResult.offsetPrice.value);
          quoteResult.requestedFor = "CARBON"
          await processCreatedQuote(quoteResult, promiseOptions);
        }
    })()
        .catch(handleMakeQuoteError)
        .finally(() => setIsLoading(false));
  };

  const processCreatedQuote = async (quoteResult: any, promiseOptions?: {cancel: boolean}) => {
    setQuote(quoteResult);
    setIsZeroDecimalCurrency(quoteResult.offsetPrice.zeroDecimalCurrency);
    setAppleGooglePayPaymentRequestData ({
      country: 'NZ',
      currency: quoteResult.offsetPrice.currency.toLowerCase(),
      total: {
        label: 'Your offset contribution',
        amount: Math.round(
          quoteResult.offsetPrice.zeroDecimalCurrency 
          ? quoteResult.offsetPrice.value 
          : quoteResult.offsetPrice.value*100),
      },
      requestPayerName: true,
      requestPayerEmail: true,
    });
    const basketResponse = await getBasketById(quoteResult.basket.id);
    if (promiseOptions?.cancel) return;
    const basketResult = basketResponse.data;
    if (basketResult) { setBasket(basketResult); }
    const purchaseResponse = await startPurchaseByQuote({ quoteId: quoteResult.quoteId, })
    if (promiseOptions?.cancel) return;
    setClientSecret(purchaseResponse?.data?.clientSecret);
  };

  useEffect(() => {
    if (quote) {
      if (skipViewItemAnalyticsEvent) {
        setSkipViewItemAnalyticEvent(false);
      } else {
        sendAnalyticsEvent('view_item', { 
          co2Kgs: quote.carbonOffsetQuantity.value.toString(),
          itemName: "Contribution",
          price: quote.offsetPrice
        })
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [quote])

  const handleMakeQuoteError = (errorResponse: any) => {
    let errorMessage;
    if (errorResponse.errors) {
      errorMessage = errorResponse.errors
        .map((e: any) => {
          if (e.code === 'NEGATIVE_NET_AMOUNT') return "Requested amount is too low. " ;
          else if (e.message && e.message.match(/^This value must be greater than or equal to 1/)) return "Requested amount is too high";
          else if (e.message) return e.message;
          else return '';
        })
        .join('')
        .trim();
    }
    if (!errorMessage) {
      errorMessage = "Unknown error occurred.";
      if (errorResponse['trace_id']) {
        errorMessage += " Please contanct CarbonClick quoting reference " + errorResponse['trace_id'];
      }
    }
    setQuotingErrorMessage(errorMessage)
  }

  useEffect(() => {
    let promiseOptions = { cancel: false };
    let timeout: number | undefined;
    if (merchantCode && selectedCurrency) {
      if (monetaryAmount) {
        const quotePrice = quote?.offsetPrice;
        if (quotePrice?.value !== monetaryAmount || quotePrice?.currency !== selectedCurrency) {
          if (quote?.carbonOffsetQuantity?.value && quote.offsetPrice?.currency !== selectedCurrency) {
            makeQuoteCo2(quote?.carbonOffsetQuantity.value, selectedCurrency, merchantCode, true, promiseOptions);
          } else {
            setQuote(null);
            setIsLoading(true);
            timeout = window.setTimeout(() => makeQuote(monetaryAmount, selectedCurrency, merchantCode, promiseOptions), 700);
          }
        }
      }
      else if (co2AmountKg) {
        if (quote?.carbonOffsetQuantity?.value !== co2AmountKg || quote?.offsetPrice?.currency !== selectedCurrency) {
          setQuote(null);
          setIsLoading(true);
          timeout = window.setTimeout(() => makeQuoteCo2(co2AmountKg, selectedCurrency, merchantCode, false, promiseOptions), 700);
        }
      } else {
        setIsLoading(false);
      }
    }
    return () => { window.clearTimeout(timeout); promiseOptions.cancel = true };
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [co2AmountKg, monetaryAmount, merchantCode, selectedCurrency])

  const onOffsetClick = () => {
    if(quote) {
      setActiveStep(1);
      sendAnalyticsEvent('begin_checkout', { co2Kgs: quote.carbonOffsetQuantity.value.toString(), itemName: "Contribution", price: quote.offsetPrice })
    }
  }

  useEffect(() => {
    if (skipToPaymentParam) {
      onOffsetClick();
      setActiveStep(1);
    }
  }, [skipToPaymentParam]);

  const onPurchaseCreated = useCallback(async (p: IPurchase) => {
    setPurchase(p);
    if (onPaymentSuccessCallbackParam) {
      (async function() {
        const postResponse = await fetch(onPaymentSuccessCallbackParam, { method: "POST" });

        // a failsafe in case the callback is for other http request method
        if (postResponse.status === 405 /* Method Not Allowed */) {
          console.warn("POST to onPaymentSuccessCallback responded with status 405");
          const allowedMethodsString = postResponse.headers.get("Allow") || "";
          const allowedMethods = allowedMethodsString.split(',').map(m => m.trim().toUpperCase());
          if (allowedMethods.includes("GET")) {
            const getResponse = await fetch(onPaymentSuccessCallbackParam, { method: "POST" });
            if (!getResponse.ok) {
              throw new Error(`GET to onPaymentSuccessCallback responded with status ${postResponse.status}`);
            }
          }
        } else if (!postResponse.ok) {
          throw new Error(`POST to onPaymentSuccessCallback responded with status ${postResponse.status}`);
        }
      })()
        .then(() => console.log("onPaymentSuccessCallback was called successfully"))
        .catch((e) => { console.error("onPaymentSuccessCallback failed"); console.error(e); });
    }
  }, [setPurchase,onPaymentSuccessCallbackParam]);

  return (
    <div className={`${styles.widgetWrapper} translate-dynamic`}>
      {(loading || !widgetConfiguration) ? (
        <div className={styles.loader}></div>
      ) : (
        <>
            {activeStep === 0 ? (
              (widgetConfiguration.entryPage.display && !entryOptionSelected) ?
                <EntryPage
                  setSelectedCurrency={setSelectedCurrency}
                  setOffsetAmount={setMonetaryAmount}
                  setEntryOptionSelected={setEntryOptionSelected}
                  makeQuote={makeQuote}
                  merchantCode={merchantCode}
                  setActiveStep={setActiveStep}
                  widgetConfiguration={widgetConfiguration}
                /> :
                <EstimationPage
                  isZeroDecimalCurrency={isZeroDecimalCurrency}
                  monetaryAmount={monetaryAmount}
                  quote={quote}
                  selectedCurrency={selectedCurrency}
                  isLoading={isLoading}
                  basket={basket}
                  merchantCode={merchantCode}
                  onOffsetClick={onOffsetClick}
                  setSelectedCurrency={setSelectedCurrency}
                  setMonetaryAmount={setMonetaryAmount}
                  widgetConfiguration={widgetConfiguration}
                  setActiveStep={setActiveStep}
                  quotingErrorMessage={quotingErrorMessage}
                  co2amountKg={co2AmountKg}
                  setCo2amountKg={setCo2AmountKg}
                />
            ) : activeStep === 1 ? (
              <PaymentPage
                programmeCode={programmeCode}
                clientSecret={clientSecret}
                quote={quote}
                merchantCode={merchantCode}
                setUserEmail={setUserEmail}
                setPurchase={onPurchaseCreated}
                setActiveStep={setActiveStep}
                setEntryOptionSelected={setEntryOptionSelected}
                onOffsetClick={onOffsetClick}
                language={language}
                appleGooglePayPaymentRequestData={appleGooglePayPaymentRequestData}
                userEmail={userEmail}
                widgetConfiguration={widgetConfiguration}
              />
            ) : (
              activeStep === 2 && (
                <PaymentConfirmationPage
                  purchase={purchase}
                  basket={basket}
                  userEmail={userEmail}
                  language={language}
                  merchantCode={merchantCode}
                  widgetConfiguration={widgetConfiguration}
                  setActiveStep={setActiveStep}
                />
              )
            )}
        </>
      )}
    </div>
  )
};

export default ContributionWidget;
